import { useSelector } from 'react-redux';
import { selectSession } from '@client/model';
import { SessionType, type NylPilotAgentMetrics } from '@shared';
import { useCallback, useMemo } from 'react';
import { useDateFilter } from './use-date-filter';
import { useAdvisorMetricsApi } from './api/use-advisor-metrics-api';
import {
  DateFilterState,
  MetricsOrganization,
  PeriodActivityFilter,
  UseAdvisorMetricsResult,
} from '../types/metrics-type';

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const INACTIVE_SEAT_STATUSES = new Set(['inactive', 'paused', 'payment_failed']);

export function useAdvisorMetrics(
  organization: MetricsOrganization = 'all',
  active = true,
): UseAdvisorMetricsResult {
  const session = useSelector(selectSession);
  const enabled = active && session.sessionType === SessionType.wizard;
  const query = useAdvisorMetricsApi(enabled);
  const data = active ? query.data : undefined;
  const isLoading = active && query.isLoading;
  const isFetching = active && query.isFetching;
  const error = active ? query.error : null;
  const {
    dateFilter,
    setPreset: setDatePreset,
    setCustomDates,
    resetFilter,
    lastMonthDates,
    lastWeekDates,
  } = useDateFilter();

  //Core Fields

  const allAdvisorDetails = useMemo(() => data?.advisorDetails || [], [data]);

  const nylMetricsAdvisorIds = useMemo(
    () => new Set(allAdvisorDetails.filter(isNylMetricsAdvisor).map(advisor => advisor.advisorId)),
    [allAdvisorDetails],
  );

  const partnerAdvisorDetails = useMemo(
    () =>
      allAdvisorDetails.filter(advisor => organization === 'all' || isNylMetricsAdvisor(advisor)),
    [allAdvisorDetails, organization],
  );

  const advisorDetails = useMemo(
    () =>
      partnerAdvisorDetails.filter(advisor =>
        isCreatedWithinDateFilter(advisor.advisorCreatedDateTime, dateFilter),
      ),
    [dateFilter, partnerAdvisorDetails],
  );

  const wizardAdvisors = useMemo(
    () => advisorDetails.filter(advisor => advisor.isWizardAdvisor),
    [advisorDetails],
  );

  const allSubscriptionDetails = useMemo(() => data?.subscriptionDetails || [], [data]);

  const activeSubscriptionAccessAdvisorIds = useMemo(() => {
    const advisorIds = new Set<string>();
    allSubscriptionDetails.forEach(subscription => {
      if (!ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.subscriptionStatus)) return;
      subscription.seats?.forEach(seat => {
        if (seat.status === 'active' && seat.advisorId) advisorIds.add(seat.advisorId);
      });
    });
    return advisorIds;
  }, [allSubscriptionDetails]);

  const inactiveNylAdvisorIds = useMemo(
    () =>
      new Set(
        allAdvisorDetails
          .filter(
            advisor =>
              isNylMetricsAdvisor(advisor) &&
              !hasPendingSetup(advisor) &&
              !activeSubscriptionAccessAdvisorIds.has(advisor.advisorId),
          )
          .map(advisor => advisor.advisorId),
      ),
    [activeSubscriptionAccessAdvisorIds, allAdvisorDetails],
  );

  const partnerSubscriptionDetails = useMemo(
    () =>
      organization === 'nyl'
        ? allSubscriptionDetails.filter(subscription =>
            isSubscriptionHistoricallyLinkedToNyl(subscription, nylMetricsAdvisorIds),
          )
        : allSubscriptionDetails,
    [allSubscriptionDetails, nylMetricsAdvisorIds, organization],
  );

  const subscriptionDetails = useMemo(
    () =>
      partnerSubscriptionDetails.filter(
        subscription =>
          (organization !== 'nyl' ||
            isSubscriptionInNylCohort(subscription, nylMetricsAdvisorIds, inactiveNylAdvisorIds)) &&
          isSubscriptionWithinDateFilter(
            subscription,
            dateFilter,
            organization,
            nylMetricsAdvisorIds,
            inactiveNylAdvisorIds,
          ),
      ),
    [
      dateFilter,
      inactiveNylAdvisorIds,
      nylMetricsAdvisorIds,
      organization,
      partnerSubscriptionDetails,
    ],
  );

  const usageDetails = useMemo(() => {
    const details = data?.usageMetrics || {};
    if (organization !== 'nyl') return details;
    return Object.fromEntries(
      Object.entries(details).filter(([advisorId]) => nylMetricsAdvisorIds.has(advisorId)),
    );
  }, [data, nylMetricsAdvisorIds, organization]);

  //Advisors

  const totalAdvisors = useMemo(() => {
    const total = advisorDetails.length || 0;
    return total;
  }, [advisorDetails]);

  const newAdvisorsCustomDates = useMemo(() => {
    if (dateFilter.preset === 'allTime') return advisorDetails;

    return advisorDetails.filter(advisor => {
      const createdAt = new Date(advisor.advisorCreatedDateTime);
      return createdAt >= dateFilter.startDate && createdAt <= dateFilter.endDate;
    });
  }, [advisorDetails, dateFilter]);

  //Subscriptions

  const activeSubscriptions = useMemo(
    () => subscriptionDetails.filter(subscription => subscription.subscriptionStatus === 'active'),
    [subscriptionDetails],
  );

  const trialSubscriptions = useMemo(
    () =>
      subscriptionDetails.filter(subscription => subscription.subscriptionStatus === 'trialing'),
    [subscriptionDetails],
  );

  const canceledSubscriptions = useMemo(
    () =>
      subscriptionDetails.filter(subscription => subscription.subscriptionStatus === 'canceled'),
    [subscriptionDetails],
  );

  const otherInactiveSubscriptions = useMemo(
    () =>
      subscriptionDetails.filter(
        subscription =>
          !ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.subscriptionStatus) &&
          subscription.subscriptionStatus !== 'canceled',
      ),
    [subscriptionDetails],
  );

  const totalActiveSubscriptions = useMemo(
    () => [...activeSubscriptions, ...trialSubscriptions],
    [activeSubscriptions, trialSubscriptions],
  );

  const totalInactiveSubscriptions = useMemo(
    () => [...canceledSubscriptions, ...otherInactiveSubscriptions],
    [canceledSubscriptions, otherInactiveSubscriptions],
  );

  const activeSubscriptionSeats = useMemo(
    () => activeSubscriptions.reduce((total, subscription) => total + subscription.totalSeats, 0),
    [activeSubscriptions],
  );

  const trialSubscriptionSeats = useMemo(
    () => trialSubscriptions.reduce((total, subscription) => total + subscription.totalSeats, 0),
    [trialSubscriptions],
  );

  const activeSubscriptionTotalSeats = activeSubscriptionSeats + trialSubscriptionSeats;

  const totalUtilizedSeats = useMemo(() => {
    const total = totalActiveSubscriptions.reduce((total, subscription) => {
      const utilizedSeatsForThisSubscription = subscription.seats
        ? subscription.seats.reduce((seatTotal, seat) => {
            return seatTotal + (seat.status === 'active' ? 1 : 0);
          }, 0)
        : 0;
      return total + utilizedSeatsForThisSubscription;
    }, 0);
    return total;
  }, [totalActiveSubscriptions]);

  const advisorHasActiveAccess = useCallback(
    (advisor: AdvisorMetrics) =>
      advisor.isWizardAdvisor || activeSubscriptionAccessAdvisorIds.has(advisor.advisorId),
    [activeSubscriptionAccessAdvisorIds],
  );

  const advisorHasPendingSetup = useCallback(hasPendingSetup, []);

  const pendingSetupAdvisors = useMemo(
    () => advisorDetails.filter(advisorHasPendingSetup),
    [advisorDetails, advisorHasPendingSetup],
  );

  const setupCompleteAdvisors = useMemo(
    () => advisorDetails.filter(advisor => !advisorHasPendingSetup(advisor)),
    [advisorDetails, advisorHasPendingSetup],
  );

  const activeAccessAdvisors = useMemo(
    () => setupCompleteAdvisors.filter(advisorHasActiveAccess),
    [setupCompleteAdvisors, advisorHasActiveAccess],
  );

  const advisorsWithoutActiveAccess = useMemo(
    () => setupCompleteAdvisors.filter(advisor => !advisorHasActiveAccess(advisor)),
    [setupCompleteAdvisors, advisorHasActiveAccess],
  );

  //Cancellations

  const cancellationsTriggeredLast7Days = useMemo(() => {
    const cancellations = partnerSubscriptionDetails.filter(subscription =>
      isCancellationTriggeredWithinDateRange(
        subscription,
        lastWeekDates.startDate,
        lastWeekDates.endDate,
      ),
    );
    return cancellations;
  }, [partnerSubscriptionDetails, lastWeekDates]);

  const cancellationsTriggeredLast30Days = useMemo(() => {
    const cancellations = partnerSubscriptionDetails.filter(subscription =>
      isCancellationTriggeredWithinDateRange(
        subscription,
        lastMonthDates.startDate,
        lastMonthDates.endDate,
      ),
    );
    return cancellations;
  }, [partnerSubscriptionDetails, lastMonthDates]);

  const cancellationsTriggeredWithinCustomDates = useMemo(() => {
    const cancellations = partnerSubscriptionDetails.filter(subscription =>
      isCancellationTriggeredWithinDateRange(
        subscription,
        dateFilter.startDate,
        dateFilter.endDate,
      ),
    );
    return cancellations;
  }, [partnerSubscriptionDetails, dateFilter]);

  //Actions

  const transformSubscriptionDetailsForDataTable = useCallback(
    (subscriptions: AdvisorSubscriptionMetrics[]) => {
      return subscriptions.map(subscription => {
        const ownerAdvisor = allAdvisorDetails.find(
          advisor => advisor.advisorId === subscription.ownerAdvisorId,
        );
        const ownerAdvisorName = ownerAdvisor
          ? `${ownerAdvisor.advisorFirstName} ${ownerAdvisor.advisorLastName}`
          : 'Unknown';
        const ownerAdvisorEmail = ownerAdvisor?.advisorEmail || 'N/A';

        const ownerSubscriptionSeat = subscription.seats?.find(
          seat => seat.advisorId === subscription.ownerAdvisorId,
        );

        const cancellationTriggered = ownerSubscriptionSeat?.metadata?.is_cancellation_triggered
          ? 'Yes'
          : 'No';
        const cancellationTriggeredAt = ownerSubscriptionSeat?.metadata?.cancellation_timestamp
          ? new Date(ownerSubscriptionSeat.metadata.cancellation_timestamp).toLocaleDateString()
          : 'N/A';
        const cancellationTriggeredReason =
          ownerSubscriptionSeat?.metadata?.cancellation_reason || 'N/A';
        const additionalFeedback = ownerSubscriptionSeat?.metadata?.cancellation_feedback || 'N/A';

        const transformedSeats = subscription.seats?.map(seat => {
          const seatAdvisor = allAdvisorDetails.find(
            advisor => advisor.advisorId === seat.advisorId,
          );
          const advisorName = seatAdvisor
            ? `${seatAdvisor.advisorFirstName} ${seatAdvisor.advisorLastName}`
            : 'Unknown';
          const advisorEmail = seatAdvisor?.advisorEmail || seat.invitedEmail || '';
          const advisorOrganization = seatAdvisor?.organizationName || 'Unknown';
          const isOwner = seat.advisorId === subscription.ownerAdvisorId;

          return {
            ...seat,
            advisorEmail,
            advisorName,
            advisorOrganization,
            isOwner,
          };
        });

        return {
          ...subscription,
          ownerAdvisorName,
          ownerAdvisorEmail,
          cancellationTriggered,
          cancellationTriggeredAt,
          cancellationTriggeredReason,
          additionalFeedback,
          seats: transformedSeats,
        };
      });
    },
    [allAdvisorDetails],
  );

  const getFilteredAdvisors = useCallback(
    (filterKey?: string | null): AdvisorMetrics[] => {
      if (!filterKey || filterKey === 'all') return advisorDetails;

      switch (filterKey) {
        case 'wizard':
          return wizardAdvisors;
        case 'activeAccess':
          return activeAccessAdvisors;
        case 'withoutActiveAccess':
          return advisorsWithoutActiveAccess;
        case 'pendingSetup':
          return pendingSetupAdvisors;
        case 'newInPeriod':
          return newAdvisorsCustomDates;
        default:
          return [];
      }
    },
    [
      advisorDetails,
      wizardAdvisors,
      activeAccessAdvisors,
      advisorsWithoutActiveAccess,
      pendingSetupAdvisors,
      newAdvisorsCustomDates,
    ],
  );

  const getFilteredSubscriptions = useCallback(
    (filterKey?: string | null) => {
      let subscriptionData = subscriptionDetails;
      if (!filterKey) return transformSubscriptionDetailsForDataTable(subscriptionData);

      switch (filterKey) {
        case 'active':
          subscriptionData = activeSubscriptions;
          break;
        case 'trialing':
          subscriptionData = trialSubscriptions;
          break;
        case 'canceled':
          subscriptionData = canceledSubscriptions;
          break;
        case 'otherInactive':
          subscriptionData = otherInactiveSubscriptions;
          break;
        case 'inactive':
          subscriptionData = totalInactiveSubscriptions;
          break;
        case 'cancellationsWeekly':
          subscriptionData = cancellationsTriggeredLast7Days;
          break;
        case 'cancellationsMonthly':
          subscriptionData = cancellationsTriggeredLast30Days;
          break;
        case 'cancellationsInPeriod':
          subscriptionData = cancellationsTriggeredWithinCustomDates;
          break;
        default:
          subscriptionData = subscriptionDetails;
      }
      return transformSubscriptionDetailsForDataTable(subscriptionData);
    },
    [
      subscriptionDetails,
      transformSubscriptionDetailsForDataTable,
      activeSubscriptions,
      trialSubscriptions,
      canceledSubscriptions,
      otherInactiveSubscriptions,
      totalInactiveSubscriptions,
      cancellationsTriggeredLast7Days,
      cancellationsTriggeredLast30Days,
      cancellationsTriggeredWithinCustomDates,
    ],
  );

  const getFilteredAdvisorsForUsage = useCallback(
    (filterKey?: string | null): AdvisorMetrics[] => {
      switch (filterKey) {
        case 'active':
        case 'trialing':
        case 'canceled':
        case 'otherInactive':
        case 'inactive':
        case 'cancellationsWeekly':
        case 'cancellationsMonthly':
        case 'cancellationsInPeriod': {
          // For subscription filters, extract advisor IDs from subscription seats
          const subscriptions = getFilteredSubscriptions(filterKey);
          const advisorIds = new Set<string>();
          subscriptions.forEach(subscription => {
            subscription.seats?.forEach(seat => {
              if (seat.advisorId) {
                advisorIds.add(seat.advisorId);
              }
            });
          });
          return advisorDetails.filter(advisor => advisorIds.has(advisor.advisorId));
        }
        default:
          return getFilteredAdvisors(filterKey);
      }
    },
    [advisorDetails, getFilteredAdvisors, getFilteredSubscriptions],
  );

  return {
    data: {
      advisorMetrics: {
        advisorDetails,
        wizardAdvisors,
        totalAdvisors,
        newAdvisorsCustomDates,
        activeAccessAdvisors,
        advisorsWithoutActiveAccess,
        pendingSetupAdvisors,
      },
      subscriptionMetrics: {
        subscriptionDetails,
        activeSubscriptions,
        trialSubscriptions,
        canceledSubscriptions,
        otherInactiveSubscriptions,
        totalActiveSubscriptions,
        totalInactiveSubscriptions,
        activeSubscriptionSeats,
        trialSubscriptionSeats,
        activeSubscriptionTotalSeats,
        totalUtilizedSeats,
      },
      cancellationMetrics: {
        cancellationsTriggeredLast7Days,
        cancellationsTriggeredLast30Days,
        cancellationsTriggeredWithinCustomDates,
      },
      usageMetrics: {
        usageDetails,
      },
      misc: {
        isLoading,
        isFetching,
        error,
        dateFilter,
      },
    },
    actions: {
      refetch: query.refetch,
      setDatePreset,
      setCustomDates,
      resetFilter,
      getFilteredAdvisors,
      getFilteredSubscriptions,
      getFilteredAdvisorsForUsage,
    },
  };
}

export function isPeriodActivityFilter(value?: string | null): value is PeriodActivityFilter {
  return value === 'activeInPeriod' || value === 'inactiveInPeriod';
}

export function isNylMetricsAdvisor(
  advisor: Pick<AdvisorMetrics, 'isNylPilotAdvisor' | 'isWizardAdvisor'>,
): boolean {
  return advisor.isNylPilotAdvisor && !advisor.isWizardAdvisor;
}

export function partitionAdvisorsByPeriodActivity(
  activeAccessAdvisors: AdvisorMetrics[],
  agents: NylPilotAgentMetrics[],
): { active: AdvisorMetrics[]; inactive: AdvisorMetrics[] } {
  const activeAdvisorIds = new Set(
    agents.filter(agent => agent.activeDays > 0).map(agent => agent.advisorId),
  );
  const active: AdvisorMetrics[] = [];
  const inactive: AdvisorMetrics[] = [];

  activeAccessAdvisors
    .filter(advisor => !advisor.isWizardAdvisor)
    .forEach(advisor => {
      (activeAdvisorIds.has(advisor.advisorId) ? active : inactive).push(advisor);
    });

  return { active, inactive };
}

export function filterAdvisorsByPeriodActivity(
  activeAccessAdvisors: AdvisorMetrics[],
  agents: NylPilotAgentMetrics[],
  filter: PeriodActivityFilter,
): AdvisorMetrics[] {
  const partition = partitionAdvisorsByPeriodActivity(activeAccessAdvisors, agents);
  return filter === 'activeInPeriod' ? partition.active : partition.inactive;
}

function isCreatedWithinDateFilter(createdAt: string, dateFilter: DateFilterState): boolean {
  if (dateFilter.preset === 'allTime') return true;

  const createdDate = new Date(createdAt);
  return (
    !Number.isNaN(createdDate.getTime()) &&
    createdDate >= dateFilter.startDate &&
    createdDate <= dateFilter.endDate
  );
}

function isSubscriptionWithinDateFilter(
  subscription: AdvisorSubscriptionMetrics,
  dateFilter: DateFilterState,
  organization: MetricsOrganization,
  nylMetricsAdvisorIds: Set<string>,
  inactiveNylAdvisorIds: Set<string>,
): boolean {
  if (dateFilter.preset === 'allTime') return true;
  if (organization === 'all') {
    return isCreatedWithinDateFilter(subscription.createdDateTime, dateFilter);
  }

  const nylOwnerCreatedWithinRange =
    isNylOwnerRelevantToSubscription(subscription, nylMetricsAdvisorIds, inactiveNylAdvisorIds) &&
    isCreatedWithinDateFilter(subscription.createdDateTime, dateFilter);
  const nylSeatAssignedWithinRange = subscription.seats?.some(
    seat =>
      isNylSeatRelevantToSubscription(
        subscription,
        seat,
        nylMetricsAdvisorIds,
        inactiveNylAdvisorIds,
      ) && isCreatedWithinDateFilter(seat.assignedDateTime ?? '', dateFilter),
  );

  return nylOwnerCreatedWithinRange || nylSeatAssignedWithinRange === true;
}

function isSubscriptionInNylCohort(
  subscription: AdvisorSubscriptionMetrics,
  nylMetricsAdvisorIds: Set<string>,
  inactiveNylAdvisorIds: Set<string>,
): boolean {
  if (isNylOwnerRelevantToSubscription(subscription, nylMetricsAdvisorIds, inactiveNylAdvisorIds)) {
    return true;
  }
  return (
    subscription.seats?.some(seat =>
      isNylSeatRelevantToSubscription(
        subscription,
        seat,
        nylMetricsAdvisorIds,
        inactiveNylAdvisorIds,
      ),
    ) === true
  );
}

function isSubscriptionHistoricallyLinkedToNyl(
  subscription: AdvisorSubscriptionMetrics,
  nylMetricsAdvisorIds: Set<string>,
): boolean {
  if (nylMetricsAdvisorIds.has(subscription.ownerAdvisorId)) return true;
  return (
    subscription.seats?.some(seat =>
      isNylSeatRelevantToSubscription(
        subscription,
        seat,
        nylMetricsAdvisorIds,
        nylMetricsAdvisorIds,
      ),
    ) === true
  );
}

function isNylOwnerRelevantToSubscription(
  subscription: AdvisorSubscriptionMetrics,
  nylMetricsAdvisorIds: Set<string>,
  inactiveNylAdvisorIds: Set<string>,
): boolean {
  if (ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.subscriptionStatus)) {
    return nylMetricsAdvisorIds.has(subscription.ownerAdvisorId);
  }
  return inactiveNylAdvisorIds.has(subscription.ownerAdvisorId);
}

function isNylSeatRelevantToSubscription(
  subscription: AdvisorSubscriptionMetrics,
  seat: AdvisorMetricSubscriptionSeats,
  nylMetricsAdvisorIds: Set<string>,
  inactiveNylAdvisorIds: Set<string>,
): boolean {
  if (!seat.advisorId) return false;
  if (ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.subscriptionStatus)) {
    return nylMetricsAdvisorIds.has(seat.advisorId) && seat.status === 'active';
  }
  return inactiveNylAdvisorIds.has(seat.advisorId) && INACTIVE_SEAT_STATUSES.has(seat.status);
}

function hasPendingSetup(advisor: AdvisorMetrics): boolean {
  return (
    !advisor.isWizardAdvisor &&
    advisor.advisorOnboardingStatus != null &&
    advisor.advisorOnboardingStatus !== 'complete'
  );
}

function isCancellationTriggeredWithinDateRange(
  subscription: AdvisorSubscriptionMetrics,
  startDate: Date,
  endDate?: Date,
): boolean {
  const ownerAdvisorId = subscription.ownerAdvisorId;
  const ownerSubscriptionSeat = subscription.seats?.find(seat => seat.advisorId === ownerAdvisorId);
  const cancellationTriggered = ownerSubscriptionSeat?.metadata?.is_cancellation_triggered;
  const cancellationTimestamp = ownerSubscriptionSeat?.metadata?.cancellation_timestamp;
  if (cancellationTriggered !== true && cancellationTriggered !== 'true') return false;
  if (!cancellationTimestamp) return false;

  const cancellationDate = new Date(cancellationTimestamp);
  if (Number.isNaN(cancellationDate.getTime())) return false;
  return cancellationDate >= startDate && (!endDate || cancellationDate <= endDate);
}
